// src/pages/LoginPage.tsx
import { useState, FormEvent, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Building2,
  Shield,
  Zap,
  Eye,
  EyeOff,
  ArrowRight,
  CheckCircle,
  TrendingUp,
  Users,
  Sparkles,
  Mail,
  Lock,
  User,
  Building,
  Phone,
  ChevronRight,
  Hammer,
  HardHat
} from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import { TEAM_API_URL } from '../utils/apiConfig';
// createCompanyOnSignup is now handled automatically by useAuth hook

interface LocationState {
  from?: { pathname: string };
}

interface SignupFormData {
  email: string;
  password: string;
  confirmPassword: string;
  fullName: string;
  companyName: string;
  role: 'builder' | 'vendor';
  phone: string;
  preferredAreaCode: string;
  agreeToTerms: boolean;
}

export default function LoginPage() {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [showEmailNotification, setShowEmailNotification] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);

  // Multi-step signup form data
  const [signupStep, setSignupStep] = useState(1);
  const [signupData, setSignupData] = useState<SignupFormData>({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    companyName: '',
    role: 'builder',
    phone: '',
    preferredAreaCode: '',
    agreeToTerms: false
  });
  const [detectedLocation, setDetectedLocation] = useState<string>('');
  const [locationDetecting, setLocationDetecting] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const state = location.state as LocationState;
  const from = state?.from?.pathname || '/dashboard';

  // Auto-detect location when signup mode is activated
  useEffect(() => {
    if (!isSignUp) return;

    const detectLocation = async () => {
      setLocationDetecting(true);
      try {
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();

        const stateToAreaCode: Record<string, string> = {
          'NY': '212', 'CA': '310', 'IL': '312', 'GA': '404',
          'TX': '512', 'FL': '305', 'WA': '206', 'MA': '617',
          'PA': '215', 'AZ': '602', 'MI': '313', 'OH': '614',
          'NC': '704', 'TN': '615', 'CO': '303', 'OR': '503',
        };

        const detectedAreaCode = stateToAreaCode[data.region_code] || '';
        const locationString = `${data.city}, ${data.region_code}`;

        if (detectedAreaCode) {
          setSignupData(prev => ({ ...prev, preferredAreaCode: detectedAreaCode }));
          setDetectedLocation(locationString);
          console.log(`📍 Detected location: ${locationString} → Area code: ${detectedAreaCode}`);
        }
      } catch (error) {
        console.error('Location detection failed:', error);
      } finally {
        setLocationDetecting(false);
      }
    };

    detectLocation();
  }, [isSignUp]);

  // Check for email verified query param
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('verified') === 'true') {
      setError('');
      setEmailVerified(true);
      window.history.replaceState({}, document.title, '/login');
    }
  }, [location]);

  // Track mouse for gradient effect
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleSignIn = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const timeoutId = setTimeout(() => {
      if (loading) {
        setError('Request timed out. Please try again.');
        setLoading(false);
      }
    }, 15000);

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      clearTimeout(timeoutId);
      if (signInError) {
        setError(signInError.message || 'Invalid credentials');
        setLoading(false);
      } else {
        setError('');
        setTimeout(() => {
          setLoading(false);
          navigate(from || '/dashboard');
        }, 100);
      }
    } catch (err: any) {
      clearTimeout(timeoutId);
      setError(err.message || 'Connection error. Please try again.');
      console.error('Login error:', err);
      setLoading(false);
    }
  };

  const handleSignUp = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (signupData.password !== signupData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (signupData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!signupData.agreeToTerms) {
      setError('Please agree to the terms and conditions');
      return;
    }

    setLoading(true);

    const timeoutId = setTimeout(() => {
      if (loading) {
        console.warn('⚠️ Signup taking longer than expected...');
        // Don't stop loading, just warn
      }
    }, 15000);

    try {
      // Just signup - no email confirmation needed
      const { data, error: signupError } = await supabase.auth.signUp({
        email: signupData.email,
        password: signupData.password,
        options: {
          data: {
            full_name: signupData.fullName,
            company_name: signupData.companyName,
            role: signupData.role,
            phone: signupData.phone,
            preferred_area_code: signupData.preferredAreaCode
          }
        }
      });

      if (signupError) throw signupError;

      console.log('✅ Signup successful! Redirecting to pricing page...');
      console.log('ℹ️ Team will be created automatically by useAuth hook');

      clearTimeout(timeoutId);
      setLoading(false);

      // Redirect to pricing page - team creation happens automatically in useAuth
      navigate('/pricing');

    } catch (err: any) {
      clearTimeout(timeoutId);
      setError(err.message || 'An error occurred during signup');
      console.error('Signup error:', err);
      setLoading(false);
    }
  };

  // If email notification is showing, display success screen
  if (showEmailNotification && isSignUp) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-950 via-gray-900 to-black flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-gray-900/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-800 p-12 max-w-md w-full text-center"
        >
          <div className="w-20 h-20 bg-green-900/30 border-2 border-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-500" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-4">Check Your Email!</h2>
          <p className="text-gray-400 mb-8">
            We've sent a confirmation email to <strong className="text-white">{signupData.email}</strong>.
            Click the link in the email to verify your account and continue to payment.
          </p>
          <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-300">
              <strong>⚠️ Check your spam/junk folder!</strong> The email might be there. Mark it as "Not Spam" or
              <button
                onClick={() => window.location.reload()}
                className="ml-1 underline font-semibold hover:text-blue-200"
              >
                resend confirmation email
              </button>
            </p>
          </div>
          <button
            onClick={() => {
              setShowEmailNotification(false);
              setIsSignUp(false);
            }}
            className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 font-semibold"
          >
            Go to Login <ArrowRight className="w-4 h-4" />
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex relative overflow-hidden bg-black">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 opacity-30 transition-all duration-100"
          style={{
            background: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px,
              rgba(59, 130, 246, 0.5) 0%,
              transparent 50%)`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-950 via-gray-900 to-black" />
        <div className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `linear-gradient(rgba(59, 130, 246, 0.1) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(59, 130, 246, 0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        />
        <div className="absolute top-20 left-20 w-96 h-96 bg-blue-600 rounded-full filter blur-3xl opacity-20 animate-pulse" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-600 rounded-full filter blur-3xl opacity-20 animate-pulse animation-delay-2000" />
      </div>

      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative z-10 items-center justify-center p-12">
        <div className="max-w-lg">
          <div className="mb-12">
            <div className="relative inline-block">
              <div className="absolute inset-0 bg-cyan-400 blur-3xl opacity-40"></div>
              <img
                src="/homequest-logo.svg"
                alt="HomeQuest Tech"
                className="relative h-32 w-auto"
              />
            </div>
            <div className="mt-4 flex items-center gap-2">
              <span className="px-3 py-1 bg-cyan-400/10 border border-cyan-400/30 rounded-full text-cyan-400 text-xs font-semibold tracking-wider">
                BETA
              </span>
              <span className="text-gray-500 text-sm">Early Access Program</span>
            </div>
          </div>

          <h2 className="text-5xl font-bold text-white mb-6 leading-tight">
            Where Land
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-600">
              Meets Logic
            </span>
          </h2>

          <p className="text-gray-400 text-lg mb-12">
            AI-powered construction platform that revolutionizes site analysis, floor plan processing, and project management for modern builders.
          </p>

          <div className="grid grid-cols-1 gap-4 mb-12">
            <div className="flex items-start gap-3">
              <div className="mt-1 p-2 bg-cyan-400/10 rounded-lg">
                <Zap className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <div className="text-white font-semibold">AI Floor Plan Analysis</div>
                <div className="text-sm text-gray-400">Instant room detection & measurements</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="mt-1 p-2 bg-cyan-400/10 rounded-lg">
                <TrendingUp className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <div className="text-white font-semibold">Smart Site Analysis</div>
                <div className="text-sm text-gray-400">Terrain evaluation & cost estimation</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="mt-1 p-2 bg-cyan-400/10 rounded-lg">
                <Users className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <div className="text-white font-semibold">Team Collaboration</div>
                <div className="text-sm text-gray-400">Real-time project management</div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 text-gray-500">
            <Shield className="w-5 h-5" />
            <span className="text-sm">256-bit encryption • Secure cloud storage</span>
          </div>
        </div>
      </div>

      {/* Right Panel - Login/Signup Form */}
      <div className="flex-1 flex items-center justify-center p-8 relative z-10">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden mb-8 text-center">
            <img
              src="/homequest-logo.svg"
              alt="HomeQuest Tech"
              className="h-24 w-auto mx-auto mb-2"
            />
            <span className="inline-block px-3 py-1 bg-cyan-400/10 border border-cyan-400/30 rounded-full text-cyan-400 text-xs font-semibold tracking-wider">
              BETA
            </span>
          </div>

          {/* Form Card */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl blur-xl opacity-20"></div>

            <div className="relative bg-gray-900/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-800 p-8">
              {/* Form Header */}
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-white mb-2">
                  {isSignUp ? 'Start Your Journey' : 'Welcome Back'}
                </h2>
                <p className="text-gray-400">
                  {isSignUp
                    ? 'Join 50,000+ construction professionals'
                    : 'Your projects are waiting'}
                </p>
              </div>

              {/* Progress Steps for Signup */}
              {isSignUp && (
                <div className="flex items-center justify-between mb-8">
                  {[1, 2, 3].map((s) => (
                    <div
                      key={s}
                      className={`flex items-center ${s < 3 ? 'flex-1' : ''}`}
                    >
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                          signupStep >= s
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-700 text-gray-400'
                        }`}
                      >
                        {signupStep > s ? <CheckCircle className="w-5 h-5" /> : s}
                      </div>
                      {s < 3 && (
                        <div
                          className={`flex-1 h-1 mx-2 transition-all ${
                            signupStep > s ? 'bg-blue-600' : 'bg-gray-700'
                          }`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Email Verified Success Message */}
              {emailVerified && (
                <div className="mb-6 p-4 bg-green-900/20 border border-green-700 rounded-xl">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                    <div>
                      <h3 className="text-green-400 font-semibold mb-1">Email Verified Successfully!</h3>
                      <p className="text-green-300/80 text-sm">
                        Your account is now active. You can sign in below.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-4 p-3 bg-red-900/20 border border-red-800 rounded-xl text-red-400 text-sm"
                >
                  {error}
                </motion.div>
              )}

              {/* SIGN IN FORM */}
              {!isSignUp && (
                <form onSubmit={handleSignIn} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all placeholder-gray-500"
                      placeholder="you@company.com"
                      autoComplete="username email"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all placeholder-gray-500 pr-12"
                        placeholder="••••••••"
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="flex items-center">
                      <input type="checkbox" className="mr-2 rounded bg-gray-800 border-gray-700" />
                      <span className="text-sm text-gray-400">Remember me</span>
                    </label>
                    <a href="#" className="text-sm text-blue-400 hover:text-blue-300 transition-colors">
                      Forgot password?
                    </a>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="relative w-full group"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl blur group-hover:blur-md transition-all duration-300"></div>
                    <div className="relative w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl font-semibold text-white flex items-center justify-center gap-2 group-hover:shadow-xl transition-all duration-300">
                      {loading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          <span>Processing...</span>
                        </>
                      ) : (
                        <>
                          <span>Sign In</span>
                          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </div>
                  </button>

                  <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                    <Shield className="w-4 h-4" />
                    <span>256-bit SSL encryption • SOC 2 Type II certified</span>
                  </div>
                </form>
              )}

              {/* MULTI-STEP SIGNUP FORM */}
              {isSignUp && (
                <form onSubmit={handleSignUp} className="space-y-4">
                  {/* Step 1: Company Info */}
                  {signupStep === 1 && (
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="space-y-4"
                    >
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Full Name
                        </label>
                        <div className="relative">
                          <User className="absolute left-3 top-3 w-5 h-5 text-gray-500" />
                          <input
                            type="text"
                            value={signupData.fullName}
                            onChange={(e) => setSignupData({ ...signupData, fullName: e.target.value })}
                            className="pl-11 w-full px-4 py-3 bg-gray-800/50 border border-gray-700 text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-500"
                            placeholder="John Doe"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Company Name
                        </label>
                        <div className="relative">
                          <Building className="absolute left-3 top-3 w-5 h-5 text-gray-500" />
                          <input
                            type="text"
                            value={signupData.companyName}
                            onChange={(e) => setSignupData({ ...signupData, companyName: e.target.value })}
                            className="pl-11 w-full px-4 py-3 bg-gray-800/50 border border-gray-700 text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-500"
                            placeholder="ABC Construction Inc."
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Phone Number
                        </label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-3 w-5 h-5 text-gray-500" />
                          <input
                            type="tel"
                            value={signupData.phone}
                            onChange={(e) => setSignupData({ ...signupData, phone: e.target.value })}
                            className="pl-11 w-full px-4 py-3 bg-gray-800/50 border border-gray-700 text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-500"
                            placeholder="(555) 123-4567"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Business Phone Area Code
                        </label>
                        {detectedLocation && (
                          <div className="mb-2 flex items-center gap-2 text-sm text-green-400">
                            <CheckCircle className="w-4 h-4" />
                            <span>Auto-detected: {detectedLocation}</span>
                          </div>
                        )}
                        {locationDetecting && (
                          <div className="mb-2 flex items-center gap-2 text-sm text-gray-400">
                            <div className="w-4 h-4 border-2 border-gray-500 border-t-blue-500 rounded-full animate-spin" />
                            <span>Detecting your location...</span>
                          </div>
                        )}
                        <select
                          value={signupData.preferredAreaCode}
                          onChange={(e) => setSignupData({ ...signupData, preferredAreaCode: e.target.value })}
                          className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Select area code (optional)</option>
                          <option value="212">212 - New York, NY</option>
                          <option value="310">310 - Los Angeles, CA</option>
                          <option value="312">312 - Chicago, IL</option>
                          <option value="404">404 - Atlanta, GA</option>
                          <option value="415">415 - San Francisco, CA</option>
                          <option value="512">512 - Austin, TX</option>
                          <option value="678">678 - Atlanta, GA (Overlay)</option>
                          <option value="770">770 - Atlanta Metro, GA</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          📞 We'll provision a local Twilio phone number with AI receptionist for your team
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => setSignupStep(2)}
                        disabled={!signupData.fullName || !signupData.companyName}
                        className="w-full bg-blue-600 text-white py-3 px-4 rounded-xl font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        Continue <ChevronRight className="w-5 h-5" />
                      </button>
                    </motion.div>
                  )}

                  {/* Step 2: Credentials */}
                  {signupStep === 2 && (
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="space-y-4"
                    >
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Email Address
                        </label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-500" />
                          <input
                            type="email"
                            value={signupData.email}
                            onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                            className="pl-11 w-full px-4 py-3 bg-gray-800/50 border border-gray-700 text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-500"
                            placeholder="john@company.com"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Password
                        </label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-500" />
                          <input
                            type="password"
                            value={signupData.password}
                            onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                            className="pl-11 w-full px-4 py-3 bg-gray-800/50 border border-gray-700 text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-500"
                            placeholder="Minimum 8 characters"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Confirm Password
                        </label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-500" />
                          <input
                            type="password"
                            value={signupData.confirmPassword}
                            onChange={(e) => setSignupData({ ...signupData, confirmPassword: e.target.value })}
                            className="pl-11 w-full px-4 py-3 bg-gray-800/50 border border-gray-700 text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-500"
                            placeholder="Re-enter your password"
                            required
                          />
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setSignupStep(1)}
                          className="flex-1 bg-gray-700 text-gray-300 py-3 px-4 rounded-xl font-semibold hover:bg-gray-600 transition-all"
                        >
                          Back
                        </button>
                        <button
                          type="button"
                          onClick={() => setSignupStep(3)}
                          disabled={!signupData.email || !signupData.password || !signupData.confirmPassword}
                          className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-xl font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          Continue <ChevronRight className="w-5 h-5" />
                        </button>
                      </div>
                    </motion.div>
                  )}

                  {/* Step 3: Role & Terms */}
                  {signupStep === 3 && (
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="space-y-4"
                    >
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-3">
                          I am a...
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => setSignupData({ ...signupData, role: 'builder' })}
                            className={`p-4 rounded-xl border-2 transition-all ${
                              signupData.role === 'builder'
                                ? 'border-blue-500 bg-blue-900/20'
                                : 'border-gray-700 bg-gray-800/30 hover:border-gray-600'
                            }`}
                          >
                            <Hammer className={`w-8 h-8 mb-2 mx-auto ${
                              signupData.role === 'builder' ? 'text-blue-400' : 'text-gray-400'
                            }`} />
                            <div className="font-semibold text-white">Builder</div>
                            <div className="text-xs text-gray-400 mt-1">
                              Manage projects & teams
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => setSignupData({ ...signupData, role: 'vendor' })}
                            className={`p-4 rounded-xl border-2 transition-all ${
                              signupData.role === 'vendor'
                                ? 'border-blue-500 bg-blue-900/20'
                                : 'border-gray-700 bg-gray-800/30 hover:border-gray-600'
                            }`}
                          >
                            <HardHat className={`w-8 h-8 mb-2 mx-auto ${
                              signupData.role === 'vendor' ? 'text-blue-400' : 'text-gray-400'
                            }`} />
                            <div className="font-semibold text-white">Vendor</div>
                            <div className="text-xs text-gray-400 mt-1">
                              Bid on projects
                            </div>
                          </button>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <input
                          type="checkbox"
                          checked={signupData.agreeToTerms}
                          onChange={(e) => setSignupData({ ...signupData, agreeToTerms: e.target.checked })}
                          className="mt-1 mr-2 rounded"
                          id="terms"
                        />
                        <label htmlFor="terms" className="text-sm text-gray-400">
                          I agree to the{' '}
                          <Link to="/terms" className="text-blue-400 hover:underline">
                            Terms of Service
                          </Link>{' '}
                          and{' '}
                          <Link to="/privacy" className="text-blue-400 hover:underline">
                            Privacy Policy
                          </Link>
                        </label>
                      </div>

                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setSignupStep(2)}
                          className="flex-1 bg-gray-700 text-gray-300 py-3 px-4 rounded-xl font-semibold hover:bg-gray-600 transition-all"
                        >
                          Back
                        </button>
                        <button
                          type="submit"
                          disabled={loading || !signupData.agreeToTerms}
                          className="flex-1 relative group"
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl blur group-hover:blur-md transition-all"></div>
                          <div className="relative py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50">
                            {loading ? (
                              <>
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                Creating Account...
                              </>
                            ) : (
                              <>
                                Create Account <Sparkles className="w-5 h-5" />
                              </>
                            )}
                          </div>
                        </button>
                      </div>
                    </motion.div>
                  )}
                </form>
              )}

              {/* Toggle sign up/sign in */}
              <div className="mt-6 text-center">
                <p className="text-gray-400">
                  {isSignUp ? 'Already have an account?' : "Don't have an account?"}
                  <button
                    type="button"
                    onClick={() => {
                      setIsSignUp(!isSignUp);
                      setError('');
                      setSignupStep(1);
                    }}
                    className="ml-2 text-blue-400 hover:text-blue-300 font-semibold transition-colors"
                  >
                    {isSignUp ? 'Sign In' : 'Sign Up'}
                  </button>
                </p>
              </div>

              {/* Enterprise SSO */}
              {!isSignUp && (
                <div className="mt-6 pt-6 border-t border-gray-800">
                  <button className="w-full py-3 px-4 bg-gray-800/50 border border-gray-700 rounded-xl text-gray-300 hover:bg-gray-800 transition-all flex items-center justify-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    <span>Enterprise SSO</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="mt-8 text-center text-xs text-gray-500">
            <p>© 2025 HomeQuest Tech™. All rights reserved.</p>
            <div className="flex items-center justify-center gap-4 mt-2">
              <a href="#" className="hover:text-gray-400 transition-colors">Terms</a>
              <span>•</span>
              <a href="#" className="hover:text-gray-400 transition-colors">Privacy</a>
              <span>•</span>
              <a href="#" className="hover:text-gray-400 transition-colors">Security</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
